import { useState, useEffect, useCallback } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Layout } from '../components/Layout'
import { hasPermission } from '../utils/permissions'
import { supabase } from '../services/supabase'
import { logActivity } from '../utils/activityLogger'
import { Save, Upload, X, Search, CheckSquare, Square, Video, FileText, Calendar, Building2, Users, Tag } from 'lucide-react'

const NotificationCreate = () => {
  const { userProfile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [schools, setSchools] = useState([])
  const [notificationCategories, setNotificationCategories] = useState([])
  const [schoolCategories, setSchoolCategories] = useState([])
  const [totalSchoolCount, setTotalSchoolCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectionMode, setSelectionMode] = useState('category') // 'category' veya 'individual'
  const [searchTerm, setSearchTerm] = useState('')
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    son_tarih: '',
    selected_categories: [],
    selected_school_categories: [],
    selected_schools: [],
    is_urgent: false,
    is_important: false,
  })
  const [attachments, setAttachments] = useState([])
  const [previewFiles, setPreviewFiles] = useState([])

  const fetchNotificationCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_name')

      if (error) throw error
      setNotificationCategories(data || [])
    } catch (error) {
      console.error('Bildirim kategorileri yüklenemedi:', error)
    }
  }

  const fetchSchoolCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('school_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_name')

      if (error) throw error
      setSchoolCategories(data || [])
      
      // Her kategori için okul sayısını hesapla
      if (data && data.length > 0) {
        const categoryCounts = await Promise.all(
          data.map(async (category) => {
            const { count, error: countError } = await supabase
              .from('school_category_relations')
              .select('*', { count: 'exact', head: true })
              .eq('category_id', category.id)
            
            if (countError) {
              console.error('Kategori okul sayısı hesaplanamadı:', countError)
              return { ...category, schoolCount: 0 }
            }
            
            return { ...category, schoolCount: count || 0 }
          })
        )
        
        setSchoolCategories(categoryCounts)
      }
    } catch (error) {
      console.error('Okul kategorileri yüklenemedi:', error)
    }
  }

  const fetchSchools = async () => {
    try {
      const { data, error } = await supabase
        .from('schools')
        .select(`
          *,
          categories:school_category_relations(
            category_id,
            category:school_categories(*)
          )
        `)
        .order('okul_adi')

      if (error) throw error
      
      console.log('Okullar yüklendi:', {
        total: data?.length || 0,
        sample: data?.[0] ? {
          id: data[0].id,
          name: data[0].okul_adi,
          categories: data[0].categories,
          categoriesStructure: data[0].categories?.[0] ? Object.keys(data[0].categories[0]) : []
        } : null
      })
      
      setSchools(data || [])
    } catch (error) {
      console.error('Okullar yüklenemedi:', error)
    }
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    const newFiles = files.map((file) => ({
      file,
      type: file.type.startsWith('image/') ? 'image' : 'video',
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }))

    setAttachments([...attachments, ...newFiles])
    setPreviewFiles([...previewFiles, ...newFiles])
  }

  const removeAttachment = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index)
    const newPreviews = previewFiles.filter((_, i) => i !== index)
    setAttachments(newAttachments)
    setPreviewFiles(newPreviews)
  }

  const uploadFile = async (file, notificationId) => {
    const fileExt = file.name.split('.').pop()
    const fileName = `${notificationId}/${Math.random()}.${fileExt}`
    const filePath = `notifications/${fileName}`

    const { error: uploadError } = await supabase.storage
      .from('notifications')
      .upload(filePath, file)

    if (uploadError) throw uploadError

    const {
      data: { publicUrl },
    } = supabase.storage.from('notifications').getPublicUrl(filePath)

    return {
      file_name: file.name,
      file_path: publicUrl,
      file_type: file.type.startsWith('image/') ? 'image' : 'video',
      file_size: file.size,
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Bildirimi oluştur
      const notificationData = {
        title: formData.title,
        description: formData.description,
        son_tarih: formData.son_tarih || null,
        is_urgent: formData.is_urgent || false,
        is_important: formData.is_important || false,
        created_by: userProfile.id,
      }

      const { data: notification, error: notifError } = await supabase
        .from('notifications')
        .insert(notificationData)
        .select()
        .single()

      if (notifError) throw notifError

      // Hedef okulları belirle
      let targetSchools = []

      if (selectionMode === 'individual' && formData.selected_schools.length > 0) {
        // Seçili okullar
        targetSchools = formData.selected_schools
      } else if (selectionMode === 'category' && formData.selected_school_categories.length > 0) {
        // Seçili kategorilere göre tüm okullar
        const { data: categoryRelations, error: relError } = await supabase
          .from('school_category_relations')
          .select('school_id')
          .in('category_id', formData.selected_school_categories)

        if (relError) throw relError
        
        // Tekrarları kaldır
        const uniqueSchoolIds = [...new Set(categoryRelations?.map((r) => r.school_id) || [])]
        targetSchools = uniqueSchoolIds
      } else {
        // Tüm okullar (hiçbir şey seçilmediyse)
        targetSchools = schools.map((s) => s.id)
      }

      // Bildirim hedeflerini ekle
      if (targetSchools.length > 0) {
        const targets = targetSchools.map((schoolId) => ({
          notification_id: notification.id,
          school_id: schoolId,
        }))

        const { error: targetError } = await supabase
          .from('notification_targets')
          .insert(targets)

        if (targetError) throw targetError
      }

      // Kategorileri ekle
      if (formData.selected_categories.length > 0) {
        console.log('Kaydedilecek kategoriler:', formData.selected_categories)
        const categoryRelations = formData.selected_categories.map((categoryId) => ({
          notification_id: notification.id,
          category_id: categoryId,
        }))

        console.log('Kategori ilişkileri:', categoryRelations)

        const { data: insertedCategories, error: categoryError } = await supabase
          .from('notification_category_relations')
          .insert(categoryRelations)
          .select()

        if (categoryError) {
          console.error('Kategori kaydetme hatası:', categoryError)
          throw categoryError
        }
        
        console.log('Kategoriler başarıyla kaydedildi:', insertedCategories)
      }

      // Dosyaları yükle
      if (attachments.length > 0) {
        setUploading(true)
        const uploadPromises = attachments.map((item) =>
          uploadFile(item.file, notification.id)
        )

        const uploadedFiles = await Promise.all(uploadPromises)

        const attachmentData = uploadedFiles.map((file) => ({
          notification_id: notification.id,
          ...file,
        }))

        const { error: attachError } = await supabase
          .from('notification_attachments')
          .insert(attachmentData)

        if (attachError) throw attachError
      }

      // Activity log
      await logActivity(
        userProfile.id,
        'notification_created',
        'notification',
        notification.id,
        `Yeni bildirim oluşturuldu: ${notificationData.title} (${targetSchools.length} okula gönderildi)`
      )

      alert('Bildirim başarıyla oluşturuldu!')
      navigate('/')
    } catch (error) {
      console.error('Bildirim oluşturma hatası:', error)
      alert('Bildirim oluşturulurken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
      setUploading(false)
    }
  }

  // Filtrelenmiş okullar
  const filteredSchools = schools.filter((school) => {
    const matchesSearch = school.okul_adi.toLowerCase().includes(searchTerm.toLowerCase())
    
    if (selectionMode === 'category' && formData.selected_school_categories.length > 0) {
      if (!school.categories || !Array.isArray(school.categories)) return false
      // Veri yapısını kontrol et: rel.category?.id veya rel.category_id
      const schoolCategoryIds = school.categories.map(rel => {
        return rel.category?.id || rel.category_id
      }).filter(Boolean)
      return matchesSearch && formData.selected_school_categories.some(catId => schoolCategoryIds.includes(catId))
    }
    return matchesSearch
  })

  // Kategoriye göre okul sayıları
  const getCategoryCount = (categoryId) => {
    // Önce schoolCategories'den schoolCount'u kontrol et
    const category = schoolCategories.find(cat => cat.id === categoryId)
    if (category && category.schoolCount !== undefined) {
      return category.schoolCount
    }
    
    // Eğer schoolCount yoksa, schools array'inden hesapla
    const count = schools.filter(s => {
      if (!s.categories || !Array.isArray(s.categories)) return false
      // Veri yapısını kontrol et: rel.category?.id veya rel.category_id
      return s.categories.some(rel => {
        const catId = rel.category?.id || rel.category_id
        return catId === categoryId
      })
    }).length
    
    return count
  }

  // Birden fazla kategori için toplam okul sayısı (tekrarları kaldırarak)
  const getTotalCategoryCount = useCallback((categoryIds) => {
    const uniqueSchoolIds = new Set()
    schools.forEach(school => {
      if (school.categories && Array.isArray(school.categories)) {
        // Veri yapısını kontrol et: rel.category?.id veya rel.category_id
        const schoolCategoryIds = school.categories.map(rel => {
          return rel.category?.id || rel.category_id
        }).filter(Boolean)
        
        if (categoryIds.some(catId => schoolCategoryIds.includes(catId))) {
          uniqueSchoolIds.add(school.id)
        }
      }
    })
    
    console.log('getTotalCategoryCount:', { 
      categoryIds, 
      totalUniqueSchools: uniqueSchoolIds.size,
      sampleSchool: schools[0] ? {
        id: schools[0].id,
        name: schools[0].okul_adi,
        categories: schools[0].categories
      } : null
    })
    
    return uniqueSchoolIds.size
  }, [schools])

  // Toplam kategori sayısını hesapla ve state'e kaydet
  const calculateTotalCategoryCount = useCallback((categoryIds) => {
    const count = getTotalCategoryCount(categoryIds)
    setTotalSchoolCount(count)
  }, [getTotalCategoryCount])

  useEffect(() => {
    if (userProfile) {
      fetchSchools()
      fetchNotificationCategories()
      fetchSchoolCategories()
    }
  }, [userProfile])
  
  // Seçili kategoriler değiştiğinde toplam okul sayısını güncelle
  useEffect(() => {
    if (formData.selected_school_categories.length > 0) {
      calculateTotalCategoryCount(formData.selected_school_categories)
    } else {
      setTotalSchoolCount(0)
    }
  }, [formData.selected_school_categories, calculateTotalCategoryCount])

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    )
  }

  if (!userProfile || !hasPermission(userProfile, 'notifications.create')) {
    return <Navigate to="/unauthorized" replace />
  }

  // Toplu seçim/seçimi kaldırma
  const toggleAllSchools = () => {
    if (formData.selected_schools.length === filteredSchools.length) {
      setFormData({ ...formData, selected_schools: [] })
    } else {
      setFormData({ ...formData, selected_schools: filteredSchools.map(s => s.id) })
    }
  }

  const toggleSchool = (schoolId) => {
    if (formData.selected_schools.includes(schoolId)) {
      setFormData({
        ...formData,
        selected_schools: formData.selected_schools.filter(id => id !== schoolId),
      })
    } else {
      setFormData({
        ...formData,
        selected_schools: [...formData.selected_schools, schoolId],
      })
    }
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Yeni Bildirim Oluştur</h1>
          <p className="text-gray-600">Okullara bildirim gönderin ve takip edin</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Temel Bilgiler */}
          <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-blue-600" />
              Temel Bilgiler
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Başlık *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Bildirim başlığını girin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  rows={6}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                  placeholder="Bildirim detaylarını girin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar size={16} />
                  Son Tarih
                </label>
                <input
                  type="date"
                  value={formData.son_tarih}
                  onChange={(e) => setFormData({ ...formData, son_tarih: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Tag size={16} />
                  Bildirim Kategorileri (Opsiyonel - Çoklu Seçim)
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
                  {notificationCategories.length === 0 ? (
                    <p className="text-sm text-gray-500">Kategori bulunamadı</p>
                  ) : (
                    <div className="space-y-2">
                      {notificationCategories.map((category) => {
                        const isChecked = formData.selected_categories.includes(category.id)
                        return (
                          <label
                            key={category.id}
                            className="flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const checked = e.target.checked
                                const categoryId = category.id
                                
                                setFormData((prevFormData) => {
                                  let newCategories
                                  if (checked) {
                                    // Eğer zaten seçili değilse ekle
                                    if (!prevFormData.selected_categories.includes(categoryId)) {
                                      newCategories = [...prevFormData.selected_categories, categoryId]
                                    } else {
                                      newCategories = prevFormData.selected_categories
                                    }
                                  } else {
                                    // Seçimi kaldır
                                    newCategories = prevFormData.selected_categories.filter((id) => id !== categoryId)
                                  }
                                  
                                  console.log('Kategori seçimi:', {
                                    category: category.display_name,
                                    checked: checked,
                                    categoryId: categoryId,
                                    newCategories: newCategories,
                                    oldCategories: prevFormData.selected_categories
                                  })
                                  
                                  return {
                                    ...prevFormData,
                                    selected_categories: newCategories,
                                  }
                                })
                              }}
                              className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                            />
                            <div className="flex items-center space-x-2 flex-1">
                              <div
                                className="w-4 h-4 rounded border border-gray-300"
                                style={{ backgroundColor: category.color || '#3B82F6' }}
                              />
                              <span className="text-sm text-gray-900">{category.display_name}</span>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
                {formData.selected_categories.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>{formData.selected_categories.length}</strong> kategori seçildi
                    </p>
                  </div>
                )}
              </div>

              {/* Acil ve Önemli Tagleri */}
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_urgent}
                    onChange={(e) => setFormData({ ...formData, is_urgent: e.target.checked })}
                    className="w-5 h-5 text-red-600 rounded border-gray-300 focus:ring-red-500"
                  />
                  <span className="text-sm font-medium text-red-600 flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-600 rounded-full"></span>
                    Acil
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_important}
                    onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                    className="w-5 h-5 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-orange-600 flex items-center gap-1">
                    <span className="w-3 h-3 bg-orange-600 rounded-full"></span>
                    Önemli
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Hedef Seçimi */}
          <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users size={20} className="text-blue-600" />
              Hedef Seçimi
            </h2>

            {/* Seçim Modu */}
            <div className="mb-6">
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setSelectionMode('category')
                    setFormData({ ...formData, selected_schools: [] })
                  }}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    selectionMode === 'category'
                      ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <Building2 size={20} className="mx-auto mb-1" />
                  <div className="text-sm font-medium">Kategori Seç</div>
                  <div className="text-xs text-gray-500 mt-1">Tüm kategoriye gönder</div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectionMode('individual')
                    setFormData({ ...formData, selected_school_categories: [] })
                  }}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    selectionMode === 'individual'
                      ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <Users size={20} className="mx-auto mb-1" />
                  <div className="text-sm font-medium">Okul Seç</div>
                  <div className="text-xs text-gray-500 mt-1">Tek tek okul seç</div>
                </button>
              </div>
            </div>

            {/* Kategori Seçimi */}
            {selectionMode === 'category' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Okul Kategorisi * (Çoklu Seçim)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {schoolCategories.length === 0 ? (
                    <div className="col-span-full text-sm text-gray-500 p-4 text-center">
                      Kategori bulunamadı
                    </div>
                  ) : (
                    schoolCategories.map((category) => {
                      const count = getCategoryCount(category.id)
                      const isChecked = formData.selected_school_categories.includes(category.id)
                      return (
                        <label
                          key={category.id}
                          className={`p-4 rounded-lg border-2 transition-all text-left cursor-pointer ${
                            isChecked
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const checked = e.target.checked
                                const categoryId = category.id
                                
                                setFormData((prevFormData) => {
                                  let newCategories
                                  if (checked) {
                                    if (!prevFormData.selected_school_categories.includes(categoryId)) {
                                      newCategories = [...prevFormData.selected_school_categories, categoryId]
                                    } else {
                                      newCategories = prevFormData.selected_school_categories
                                    }
                                  } else {
                                    newCategories = prevFormData.selected_school_categories.filter((id) => id !== categoryId)
                                  }
                                  
                                  // Toplam okul sayısını güncelle
                                  calculateTotalCategoryCount(newCategories)
                                  
                                  return {
                                    ...prevFormData,
                                    selected_school_categories: newCategories,
                                    selected_schools: [],
                                  }
                                })
                              }}
                              className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer mt-0.5"
                            />
                            <div className="flex-1">
                              <div className="font-medium">{category.display_name}</div>
                              <div className="text-xs text-gray-500 mt-1">{count} okul</div>
                            </div>
                          </div>
                        </label>
                      )
                    })
                  )}
                </div>
                {formData.selected_school_categories.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>{formData.selected_school_categories.length}</strong> kategori seçildi. 
                      Toplam <strong>{totalSchoolCount}</strong> okula bildirim gönderilecek.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tek Tek Okul Seçimi */}
            {selectionMode === 'individual' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Okul Seçimi
                  </label>
                  {filteredSchools.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleAllSchools}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                      {formData.selected_schools.length === filteredSchools.length ? (
                        <>
                          <CheckSquare size={16} />
                          Tümünü Kaldır
                        </>
                      ) : (
                        <>
                          <Square size={16} />
                          Tümünü Seç
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Arama */}
                <div className="mb-4 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Okul adı veya kategori ile ara..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Seçilen Okul Sayısı */}
                {formData.selected_schools.length > 0 && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">
                      <strong>{formData.selected_schools.length}</strong> okul seçildi
                    </p>
                  </div>
                )}

                {/* Okul Listesi */}
                <div className="max-h-80 overflow-y-auto border border-gray-300 rounded-lg p-4 space-y-2">
                  {filteredSchools.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Okul bulunamadı</p>
                    </div>
                  ) : (
                    filteredSchools.map((school) => (
                      <label
                        key={school.id}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                          formData.selected_schools.includes(school.id)
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-white border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.selected_schools.includes(school.id)}
                          onChange={() => toggleSchool(school.id)}
                          className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{school.okul_adi}</div>
                          <div className="text-xs text-gray-500 flex flex-wrap gap-1 mt-1">
                            {school.categories && Array.isArray(school.categories) && school.categories.length > 0 ? (
                              school.categories.map((rel, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                                  {rel.category?.display_name || 'Kategori yok'}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-400">Kategori yok</span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Dosya Ekleme */}
          <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Upload size={20} className="text-blue-600" />
              Dosya Ekleme
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fotoğraf/Video Ekle
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload size={32} className="text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    Dosya seçmek için tıklayın veya sürükleyin
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Fotoğraf veya video yükleyebilirsiniz
                  </span>
                </label>
              </div>

              {previewFiles.length > 0 && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {previewFiles.map((item, index) => (
                    <div key={index} className="relative group">
                      {item.type === 'image' && item.preview ? (
                        <div className="relative">
                          <img
                            src={item.preview}
                            alt={`Preview ${index}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-300"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-all"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-32 bg-gray-200 rounded-lg border border-gray-300 flex flex-col items-center justify-center relative group">
                          <Video size={24} className="text-gray-500 mb-1" />
                          <span className="text-xs text-gray-500">Video</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-all"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                      <div className="mt-1 text-xs text-gray-500 truncate">
                        {item.file.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Butonlar */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
            >
              {uploading ? (
                <>
                  <Upload size={18} className="animate-spin" />
                  <span>Yükleniyor...</span>
                </>
              ) : loading ? (
                <>
                  <Save size={18} className="animate-pulse" />
                  <span>Kaydediliyor...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>Bildirim Oluştur</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  )
}

export default NotificationCreate
